import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ModuleOverride {
  module_id: string;
  enabled: boolean;
}

interface BulkAssignPlanParams {
  action: 'assign_plan_to_workspaces';
  org_id?: string;
  workspace_ids: string[];
  plan_code: string;
}

interface BulkSetOverridesParams {
  action: 'set_overrides_for_workspaces';
  org_id?: string;
  workspace_ids: string[];
  overrides: ModuleOverride[];
}

type BulkOperationParams = BulkAssignPlanParams | BulkSetOverridesParams;

interface BulkOperationResult {
  success: boolean;
  successCount: number;
  errorCount: number;
  errors?: Array<{ workspace_id: string; error: string }>;
  plan?: any;
  appliedOverrides?: ModuleOverride[];
}

export function useBulkWorkspaces() {
  const queryClient = useQueryClient();

  const bulkMutation = useMutation({
    mutationFn: async (params: BulkOperationParams): Promise<BulkOperationResult> => {
      console.log('[useBulkWorkspaces] Bulk operation:', params);
      
      const { data, error } = await supabase.functions.invoke('workspaces-bulk', {
        body: params
      });

      if (error) {
        console.error('[useBulkWorkspaces] Error:', error);
        throw new Error(error.message || 'Failed to perform bulk operation');
      }

      if (!data) {
        throw new Error('No data received from bulk operation');
      }

      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate entitlements for all affected workspaces
      variables.workspace_ids.forEach(workspaceId => {
        queryClient.invalidateQueries({ queryKey: ['entitlements', 'workspace', workspaceId] });
        queryClient.invalidateQueries({ queryKey: ['workspace-modules', workspaceId] });
      });
      
      const { successCount, errorCount } = data;
      
      if (errorCount === 0) {
        toast.success(`Bulk operation completed successfully for ${successCount} workspaces`);
      } else if (successCount > 0) {
        toast.warning(`Bulk operation completed with ${successCount} successes and ${errorCount} errors`);
      } else {
        toast.error(`Bulk operation failed for all ${errorCount} workspaces`);
      }
    },
    onError: (error: Error) => {
      console.error('[useBulkWorkspaces] Error:', error);
      toast.error(error.message || 'Failed to perform bulk operation');
    },
    retry: 0
  });

  const assignPlanToWorkspaces = (workspaceIds: string[], planCode: string, orgId?: string) => {
    bulkMutation.mutate({
      action: 'assign_plan_to_workspaces',
      org_id: orgId,
      workspace_ids: workspaceIds,
      plan_code: planCode
    });
  };

  const setOverridesForWorkspaces = (workspaceIds: string[], overrides: ModuleOverride[], orgId?: string) => {
    bulkMutation.mutate({
      action: 'set_overrides_for_workspaces',
      org_id: orgId,
      workspace_ids: workspaceIds,
      overrides
    });
  };

  return {
    assignPlanToWorkspaces,
    setOverridesForWorkspaces,
    bulkMutation: bulkMutation.mutate,
    isLoading: bulkMutation.isPending,
    error: bulkMutation.error,
    data: bulkMutation.data
  };
}